/**
 * Geteilte Konstanten für Oberfläche und Backend.
 *
 * Dieses Modul hat **keine Importe**. Das ist keine Kosmetik: Die Web-App holt
 * sich diese Werte über `@epasu/core/constants`, und jeder Import hier würde
 * über die Kette mit in den Browser-Bundle wandern — `better-sqlite3`,
 * `node:crypto`, Dateisystem. Der Build bricht dann ab.
 *
 * Alles, was Laufzeitlogik braucht, gehört woandershin. Hier stehen nur Listen
 * und Beschriftungen.
 */

/* -------------------------------------------------------------------------- */
/*  Maschinenschlüssel                                                        */
/* -------------------------------------------------------------------------- */

/** Scope, den der Lead-Scraper braucht. */
export const SCOPE_INGEST = 'ingest';

/**
 * Scope des Agent-Zugangs (MCP).
 *
 * Getrennt von `ingest`, damit ein abhandengekommener Scraper-Schluessel nicht
 * an Kontakte und Mails kommt — und umgekehrt ein Agent-Schluessel nicht
 * einliefern kann. Was ein Agent-Schluessel darf, steht in `AGENT_ALLOWLIST`
 * im Vertrag.
 */
export const SCOPE_AGENT = 'crm:agent';

/* -------------------------------------------------------------------------- */
/*  Aufträge                                                                  */
/* -------------------------------------------------------------------------- */

export const ORDER_STATUSES = [
  'offen',
  'in_arbeit',
  'geliefert',
  'abgerechnet',
  'storniert',
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  offen: 'Offen',
  in_arbeit: 'In Arbeit',
  geliefert: 'Geliefert',
  abgerechnet: 'Abgerechnet',
  storniert: 'Storniert',
};

export function isOrderStatus(value: unknown): value is OrderStatus {
  return (
    typeof value === 'string' && (ORDER_STATUSES as readonly string[]).includes(value)
  );
}

/* -------------------------------------------------------------------------- */
/*  Firmen                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Wo eine Firma steht. Abgeleitet aus Kundeneintrag und Deals, nie gespeichert
 * — die Ableitung steht in `organizations.ts`. `ruht` und `ehemalig` sind
 * Unterstatus des Kunden, die zum Firmenstatus wurden: so bleibt es ein Chip je
 * Firma, und „zeig mir die ruhenden Kunden“ ist eine Listenfrage.
 */
export const ORGANIZATION_STATUSES = [
  'lead',
  'im_gespraech',
  'kunde',
  'ruht',
  'ehemalig',
  'verloren',
] as const;

export type OrganizationStatus = (typeof ORGANIZATION_STATUSES)[number];

export const ORGANIZATION_STATUS_LABELS: Record<OrganizationStatus, string> = {
  lead: 'Lead',
  im_gespraech: 'In Gespräch',
  kunde: 'Kunde',
  ruht: 'Ruht',
  ehemalig: 'Ehemalig',
  verloren: 'Verloren',
};

export function isOrganizationStatus(value: unknown): value is OrganizationStatus {
  return (
    typeof value === 'string' &&
    (ORGANIZATION_STATUSES as readonly string[]).includes(value)
  );
}

/* -------------------------------------------------------------------------- */
/*  Automations                                                               */
/* -------------------------------------------------------------------------- */

export type AutomationTrigger =
  | 'email.received'
  | 'email.no_reply_since'
  | 'deal.stage_changed'
  | 'order.status_changed'
  | 'project.status_changed';

export const AUTOMATION_TRIGGERS: AutomationTrigger[] = [
  'email.received',
  'email.no_reply_since',
  'deal.stage_changed',
  'order.status_changed',
  'project.status_changed',
];

export type AutomationActionType =
  | 'create_task'
  | 'create_order'
  | 'suppress_email'
  | 'log';

export const AUTOMATION_ACTIONS: AutomationActionType[] = [
  'create_task',
  'create_order',
  'suppress_email',
  'log',
];

/* -------------------------------------------------------------------------- */
/*  Kunden                                                                    */
/* -------------------------------------------------------------------------- */

export const CUSTOMER_STATUSES = ['aktiv', 'ruht', 'ehemalig'] as const;

export type CustomerStatus = (typeof CUSTOMER_STATUSES)[number];

export const CUSTOMER_STATUS_LABELS: Record<CustomerStatus, string> = {
  aktiv: 'Aktiv',
  ruht: 'Ruht',
  ehemalig: 'Ehemalig',
};

export function isCustomerStatus(value: unknown): value is CustomerStatus {
  return (
    typeof value === 'string' &&
    (CUSTOMER_STATUSES as readonly string[]).includes(value)
  );
}

/* -------------------------------------------------------------------------- */
/*  Projekte                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Kundenprojekt oder internes Vorhaben.
 *
 * Ein ausdrücklicher Wert statt einer Prüfung auf `customer_id IS NULL`:
 * die Null-Prüfung wäre eine Regel, die jede Abfrage, jede Liste und jeder
 * Filter neu kennen müsste.
 */
export const PROJECT_KINDS = ['kunde', 'intern'] as const;

export type ProjectKind = (typeof PROJECT_KINDS)[number];

export const PROJECT_KIND_LABELS: Record<ProjectKind, string> = {
  kunde: 'Kundenprojekt',
  intern: 'Intern',
};

export function isProjectKind(value: unknown): value is ProjectKind {
  return (
    typeof value === 'string' && (PROJECT_KINDS as readonly string[]).includes(value)
  );
}

export const PROJECT_STATUSES = [
  'planung',
  'in_arbeit',
  'wartet_kunde',
  'abgeschlossen',
  'abgebrochen',
] as const;

export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  planung: 'Planung',
  in_arbeit: 'In Arbeit',
  wartet_kunde: 'Wartet auf Kunde',
  abgeschlossen: 'Abgeschlossen',
  abgebrochen: 'Abgebrochen',
};

/**
 * Endzustaende. Projekte darin zaehlen nicht als aktiv, stehen im Board
 * rechts und setzen `closed_at`.
 */
export const CLOSED_PROJECT_STATUSES: readonly ProjectStatus[] = [
  'abgeschlossen',
  'abgebrochen',
];

/**
 * Zielgruppen (`audiences.key`), für die es keine Projektarbeit gibt. Fotografen
 * gehören ins Partnernetzwerk: die Übernahme legt dort kein Projekt an, und die
 * Akte zeigt keinen Projekte-Reiter. Eine Stelle für beides, damit Dialog und
 * Akte nicht auseinanderlaufen.
 */
export const ZIELGRUPPEN_OHNE_PROJEKT: readonly string[] = ['photographers'];

/**
 * Nach wie vielen Tagen ohne Aktivität ein laufendes Projekt als
 * eingeschlafen gilt.
 *
 * Vierzehn, weil ein Projekt, an dem zwei Wochen nichts passiert ist,
 * entweder wartet — dafür gibt es den Status „Wartet auf Kunde“ — oder
 * übersehen wurde. Kürzer würde bei jedem Urlaub Alarm schlagen, länger
 * kommt zu spät.
 */
export const PROJECT_STALE_DAYS = 14;

/**
 * Warum ein Projekt in Schieflage ist.
 *
 * Mehrere können gleichzeitig zutreffen; die Oberfläche zeigt alle.
 */
export const PROJECT_RISKS = ['ueberfaellig', 'phase_ueberfaellig', 'eingeschlafen', 'ueber_budget'] as const;

export type ProjectRisk = (typeof PROJECT_RISKS)[number];

export const PROJECT_RISK_LABELS: Record<ProjectRisk, string> = {
  ueberfaellig: 'Frist überschritten',
  phase_ueberfaellig: 'Phase überfällig',
  eingeschlafen: `Seit ${PROJECT_STALE_DAYS} Tagen nichts passiert`,
  ueber_budget: 'Über Budget',
};

export function isProjectStatus(value: unknown): value is ProjectStatus {
  return (
    typeof value === 'string' &&
    (PROJECT_STATUSES as readonly string[]).includes(value)
  );
}

/* -------------------------------------------------------------------------- */
/*  Aufgaben                                                                  */
/* -------------------------------------------------------------------------- */

export const TASK_STATUSES = ['offen', 'in_arbeit', 'erledigt'] as const;

export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  offen: 'Offen',
  in_arbeit: 'In Arbeit',
  erledigt: 'Erledigt',
};

/**
 * Der einzige Endzustand. `doneAt` haengt daran — gesetzt beim Erledigen,
 * geleert beim Wiederaufmachen, dieselbe Regel wie `closedAt` am Projekt.
 */
export const TASK_STATUS_DONE: TaskStatus = 'erledigt';

export function isTaskStatus(value: unknown): value is TaskStatus {
  return (
    typeof value === 'string' && (TASK_STATUSES as readonly string[]).includes(value)
  );
}

/* -------------------------------------------------------------------------- */
/*  Suche                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Kürzer wird nicht gesucht: ein einzelnes Zeichen trifft fast alles und
 * kostet sechs Abfragen.
 *
 * Steht hier und nicht bei `searchAll`, weil die Suchleiste denselben Wert
 * braucht — und ein Import aus dem Dienst zöge das halbe Backend in den
 * Browser-Bundle, siehe oben.
 */
export const MIN_SEARCH_TERM = 2;

/* -------------------------------------------------------------------------- */
/*  Erstansprache und Nachfassen                                              */
/* -------------------------------------------------------------------------- */

/**
 * Art einer Nachricht.
 *
 * `outreach` ist die Erstansprache eines Kontakts, `followup` das Nachfassen
 * darauf. Alles uebrige ist `normal` — Antworten auf Kundenpost, Anhaenge
 * hinterherschicken, der ganze gewoehnliche Schriftverkehr.
 */
export const MESSAGE_KINDS = ['normal', 'outreach', 'followup'] as const;

export type MessageKind = (typeof MESSAGE_KINDS)[number];

export function isMessageKind(value: unknown): value is MessageKind {
  return (
    typeof value === 'string' && (MESSAGE_KINDS as readonly string[]).includes(value)
  );
}

/**
 * Zustand der Nachfass-Uhr an einer Erstansprache.
 *
 * `offen` heisst, die Uhr laeuft; `faellig`, dass die Wiedervorlage existiert
 * und auf ihre Erledigung wartet. Die drei uebrigen sind Endzustaende: es kam
 * eine Antwort, die Stufen sind aufgebraucht, oder jemand hat abgebrochen.
 */
export const FOLLOWUP_STATES = [
  'offen',
  'faellig',
  'beantwortet',
  'kalt',
  'gestoppt',
] as const;

export type FollowupState = (typeof FOLLOWUP_STATES)[number];

export const FOLLOWUP_STATE_LABELS: Record<FollowupState, string> = {
  offen: 'Uhr läuft',
  faellig: 'Fällig',
  beantwortet: 'Beantwortet',
  kalt: 'Kalt',
  gestoppt: 'Gestoppt',
};

export function isFollowupState(value: unknown): value is FollowupState {
  return (
    typeof value === 'string' && (FOLLOWUP_STATES as readonly string[]).includes(value)
  );
}

/**
 * Abstaende in Tagen bis zum naechsten Nachfassen, je Stufe.
 *
 * Index 0 gilt fuer die Erstansprache: drei Tage spaeter ist das erste
 * Nachfassen dran. Danach waechst der Abstand. Nach der letzten Stufe ist
 * Ruhe — ein viertes Nachfassen bringt niemanden zum Antworten, der es nach
 * drei Anlaeufen nicht getan hat.
 */
export const FOLLOWUP_STEP_DAYS: readonly number[] = [3, 7, 14];

/** Zahl der moeglichen Nachfass-Stufen. */
export const FOLLOWUP_MAX_STEP = FOLLOWUP_STEP_DAYS.length;

/* -------------------------------------------------------------------------- */
/*  Lebenszyklus eines Kontakts                                               */
/* -------------------------------------------------------------------------- */

/**
 * Ein Lead ist eine kalt angeschriebene Adresse, ein Kontakt jemand, mit dem
 * gearbeitet wird. Der Unterschied steuert ausschliesslich, wo die Zeile
 * sichtbar ist — nicht, was mit ihr erlaubt ist.
 */
export const CONTACT_LIFECYCLES = ['lead', 'kontakt'] as const;

export type ContactLifecycle = (typeof CONTACT_LIFECYCLES)[number];

export const CONTACT_LIFECYCLE_LABELS: Record<ContactLifecycle, string> = {
  lead: 'Lead',
  kontakt: 'Kontakt',
};

export function isContactLifecycle(value: unknown): value is ContactLifecycle {
  return (
    typeof value === 'string' &&
    (CONTACT_LIFECYCLES as readonly string[]).includes(value)
  );
}

/* -------------------------------------------------------------------------- */
/*  Kampagnen                                                                 */
/* -------------------------------------------------------------------------- */

export const CAMPAIGN_STATUSES = ['entwurf', 'aktiv', 'pausiert', 'beendet'] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  entwurf: 'Entwurf',
  aktiv: 'Aktiv',
  pausiert: 'Pausiert',
  beendet: 'Beendet',
};

/**
 * `wird_versendet` ist der beanspruchte Zustand zwischen Pruefung und
 * SMTP-Antwort. Bleibt eine Zeile darin stehen, ist der Prozess dazwischen
 * gestorben und niemand weiss, ob die Mail draussen ist — sie wird deshalb
 * nicht automatisch aufgeloest, sondern gefragt.
 */
export const CAMPAIGN_MEMBER_STATES = [
  'offen',
  'wird_versendet',
  'versendet',
  'uebersprungen',
  'entfernt',
  'fehlgeschlagen',
] as const;
export type CampaignMemberState = (typeof CAMPAIGN_MEMBER_STATES)[number];

export const CAMPAIGN_MEMBER_STATE_LABELS: Record<CampaignMemberState, string> = {
  offen: 'Offen',
  wird_versendet: 'Versand unklar',
  versendet: 'Versendet',
  uebersprungen: 'Übersprungen',
  entfernt: 'Entfernt',
  fehlgeschlagen: 'Fehlgeschlagen',
};

export const REPLY_SENTIMENTS = ['positiv', 'negativ'] as const;
export type ReplySentiment = (typeof REPLY_SENTIMENTS)[number];
